/**
 * Shifts 3–8: the campaign body (plan A6, cut from twelve shifts to eight 2026-07-30).
 *
 * The cut removed Long Shadows, Bank Fog, Consignment and All Of It — the shifts that
 * introduced nothing another shift didn't already carry. What was worth keeping moved:
 * the torpedo consignment and the corvette foreshadow live in Making Up now, and Grey
 * Hull gained a mini-sub so it is the true combined-arms shift the campaign closes its
 * human half on. Every surviving shift introduces exactly one new idea:
 * gun → searchlight → FACs/torpedo → the unanswerable sub → the hunt → the corvette →
 * the creature → the Kraken.
 *
 * Seeds are frozen at their pre-cut values ('shift-06' on index 3, and so on): renaming
 * a seed re-rolls the shift's whole RNG stream, and with it spawn jitter Adam has
 * already approved.
 *
 * Three rules hold across all of them, each learned by getting it wrong first:
 *
 * 1. **Fights are staged inside the gun's envelope.** The 30 mm scatters 2.5 mrad, which
 *    lands roughly 60% of rounds on a 9 m boat at 1 km, 28% at 2 km and 7% at 4 km.
 *    Anything staged beyond ~2.5 km asks the player to do something the weapon cannot.
 *
 * 2. **Waves are gated on the protected ship's position, never on the last wave dying.**
 *    Chaining to `whenResolved` quietly punishes accuracy: clear a pack quickly and the
 *    next arrives while the convoy is still far out, where it cannot be defended. Gating
 *    on range puts each fight at a fixed, defensible distance however the last one went.
 *
 * 3. **Timeouts are liveness fallbacks, not pacing.** They exist so a shift cannot wedge
 *    when the ship a beat is watching gets sunk on the way in. Set tight they expire
 *    early and silently defeat the range condition they are attached to.
 *
 * Every bearing sits inside APPROACH_ARC (-76..+2). The rig masks everything else, and a
 * vessel arriving behind the derrick is one the player can neither watch nor shoot along.
 */

import type { ShiftScript } from '../schema';
import { openingFor, openingBeats } from './openings';

/**
 * FACs and a rising sea: the shift that teaches shooting from a moving platform.
 *
 * Fast attack craft serpentine in, throw a rocket volley and run — so they punish a gunner
 * who tracks smoothly and reward one who leads the jink. The sea state does the rest: the
 * gun's dispersion grows with the swell, and this is where that stops being a number.
 *
 * Since the cut this shift also carries the torpedo consignment (it was Consignment's
 * whole reason to exist): Kestrel brings the launcher in through the rising storm, the
 * waves hung off her range are the pincer that used to be a shift of its own, and the
 * grey hull on the horizon is mentioned here — three watches before she closes.
 */
const shift03: ShiftScript = {
  index: 3,
  name: 'Making Up',
  weather: 'overcastDay',
  seed: 'shift-06',
  maxDuration: 1600,
  beats: [
    { kind: 'objective', trigger: { at: 0 }, text: 'Weather making up — escort the convoy' },
    {
      kind: 'radio', trigger: { at: 3 }, speaker: 'RIG CONTROL',
      text: 'Glass is falling. Get them in before it does.',
      read: '[brisk] Glass is falling. [firmly] Get them in before it does.',
    },
    {
      kind: 'spawn', trigger: { at: 6 }, tag: 'tanker-1', vessel: 'tanker',
      count: 1, rangeKm: 6.5, bearingDeg: -22, cargo: 'oil',
    },
    // Opening probe — see ./openings.
    {
      kind: 'radio', trigger: { at: openingFor(3).radioAt }, speaker: 'RADAR',
      text: 'Two small craft running in ahead of the weather. Port bow.',
      read: '[alert] Two small craft running in ahead of the weather. Port bow.',
    },
    ...openingBeats(3),
    /*
     * 240 s, not 420. The sea state is the shift's whole subject — dispersion grows with
     * it — so the storm has to actually arrive while there is still something to shoot
     * at. The old blend was longer than the shift and finished after the last FAC died.
     */
    { kind: 'weather', trigger: { at: 45 }, preset: 'storm', overSeconds: 240 },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'tanker-1', km: 2.8 }, timeout: 800 },
      tag: 'fac-a', vessel: 'fac', count: 1, rangeKm: 2.3, bearingDeg: -52,
    },
    {
      kind: 'radio', trigger: { whenWithin: { tag: 'tanker-1', km: 2.8 }, timeout: 800 },
      speaker: 'RADAR', text: 'Fast mover, twenty-three metres a second. He will not sit still for you.',
      read: '[urgent] Fast mover, twenty-three metres a second. [wry] He will not sit still for you.',
    },
    /*
     * The pair and a skiff wave on one gate, sixty degrees apart. A fast mover that
     * jinks is already a hard aiming problem; a fast mover you have to leave in order to
     * answer something else is the shift's actual difficulty.
     */
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'tanker-1', km: 1.6 }, timeout: 900 },
      tag: 'fac-b', vessel: 'fac', count: 2, rangeKm: 2.2, bearingDeg: -12, spreadDeg: 18,
    },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'tanker-1', km: 2.2 }, timeout: 900 },
      tag: 'pack-b', vessel: 'gunboat', count: 1, rangeKm: 1.9, bearingDeg: -60,
    },
    /*
     * The consignment (moved here whole from the cut shift 5). The launcher rides a hull
     * that can be sunk, through the worst water the campaign has shown so far — and the
     * waves below are already hung off her range, so defending the delivery is the
     * shift's back half without a line of new structure.
     */
    {
      kind: 'spawn', trigger: { at: 200 }, tag: 'supply-1', vessel: 'supply',
      count: 1, rangeKm: 5, bearingDeg: -44, cargo: 'torpedo',
    },
    {
      kind: 'radio', trigger: { at: 204 }, speaker: 'RIG CONTROL',
      text: 'Kestrel is bringing your torpedo launcher. Everything else is secondary.',
      read: '[firmly] Kestrel is bringing your torpedo launcher. [emphatic] Everything else is secondary.',
    },
    // The first mixed-class wave in the campaign, and it closes the shift loud.
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'supply-1', km: 2.4 }, timeout: 900 },
      tag: 'pack-c', vessel: 'skiff', count: 2, rangeKm: 1.7, bearingDeg: -64, spreadDeg: 14,
    },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'supply-1', km: 1.8 }, timeout: 900 },
      tag: 'fac-c', vessel: 'fac', count: 1, rangeKm: 2.0, bearingDeg: -26,
    },
    /*
     * Radio only, and nothing spawns: shift 6's corvette stops being a surprise from
     * nowhere and becomes a thing the player was told about three watches earlier —
     * seconds after hearing what Kestrel is carrying for her.
     */
    {
      kind: 'radio', trigger: { whenWithin: { tag: 'tanker-1', km: 1.4 }, timeout: 900 },
      speaker: 'RADAR',
      text: 'Grey hull, hull down on the horizon. She is not closing. She is counting.',
      read: '[uneasy] Grey hull, hull down on the horizon. [pause] She is not closing. [quietly] She is counting.',
    },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'supply-1', km: 1.2 }, timeout: 500 },
      tag: 'closer', vessel: 'gunboat', count: 1, rangeKm: 1.4, bearingDeg: -40,
    },
  ],
};

/**
 * The underwater teaser: something is out there and none of your sensors can find it.
 *
 * Deliberately not a fair fight, because it is not a fight. The mini-sub arrives before
 * the tools to answer it do, so the shift's content is the dread of an unanswerable
 * threat — a wake with no source, a tanker taking damage from nowhere. The projector and
 * hydrophone land mid-shift, and the encounter only becomes winnable once they do.
 */
const shift04: ShiftScript = {
  index: 4,
  name: 'Something Under',
  weather: 'night',
  seed: 'shift-07',
  maxDuration: 1700,
  beats: [
    { kind: 'objective', trigger: { at: 0 }, text: 'Night collection' },
    {
      kind: 'spawn', trigger: { at: 5 }, tag: 'tanker-1', vessel: 'tanker',
      count: 1, rangeKm: 6, bearingDeg: -16, cargo: 'oil',
    },
    /*
     * Opening probe — see ./openings. First contact was at 160 s without it.
     *
     * A surface pair, and deliberately ordinary: the shift's own idea arrives at 150 s
     * and is worth nothing if the player has spent the first two minutes bored rather
     * than settled. Something familiar first makes the thing that follows strange.
     */
    {
      kind: 'radio', trigger: { at: openingFor(4).radioAt }, speaker: 'RADAR',
      text: 'Two boats, port bow, close in. Ordinary enough.',
      read: '[calm] Two boats, port bow, close in. [pause] Ordinary enough.',
    },
    ...openingBeats(4),
    {
      kind: 'radio', trigger: { at: 150 }, speaker: 'SONAR',
      text: 'I have... something. Bearing green four zero. No surface return.',
      read: '[hesitant] I have... [pause] something. Bearing green four zero. [quietly] No surface return.',
    },
    {
      kind: 'spawn', trigger: { at: 160 }, tag: 'sub-a', vessel: 'minisub',
      count: 1, rangeKm: 1.9, bearingDeg: -40,
    },
    {
      kind: 'objective', trigger: { at: 162 }, text: 'Unidentified submerged contact',
    },
    /*
     * The dread stretch keeps its length and gains a voice.
     *
     * This is the one silence in the campaign that is content rather than a defect: the
     * shift's subject is a threat the player has no tool for, and taking the wait away
     * would take the subject with it. But silence with nothing in it reads as a game that
     * has stopped working, so the channel keeps confirming that the emptiness is the
     * thing — a contact that fades before it resolves, a wake with no source, a hull
     * reporting damage nobody saw arrive.
     */
    {
      kind: 'radio', trigger: { at: 200 }, speaker: 'SONAR',
      text: 'Lost it. It was there and then it was not. Still nothing on the surface.',
      read: '[unsettled] Lost it. [pause] It was there and then it was not. [quietly] Still nothing on the surface.',
    },
    {
      kind: 'radio', trigger: { at: 240 }, speaker: 'RIG CONTROL',
      text: 'Ardent reports flooding. Nobody saw what hit her.',
      read: '[alarmed] Ardent reports flooding. [pause] [quietly] Nobody saw what hit her.',
    },
    {
      kind: 'radio', trigger: { at: 285 }, speaker: 'RADAR',
      text: 'There is a wake off her starboard side. I have nothing making it.',
      read: '[whispers] There is a wake off her starboard side. [pause] I have nothing making it.',
    },
    /*
     * Two skiffs into the middle of it. Familiarity is what makes the strange thing
     * strange — and it gives the gun something to do while the answer to the real
     * problem is still on a boat somewhere.
     */
    {
      kind: 'spawn', trigger: { at: 250 }, tag: 'harass', vessel: 'skiff',
      count: 2, rangeKm: 1.6, bearingDeg: -66, spreadDeg: 12,
    },
    /*
     * The hydrophone itself, not a mixed manifest with a radio line about one.
     *
     * This beat has always been the shift's turning point in the fiction — the sub is
     * unanswerable until Kestrel lands the set — and it carried `mixed`, which is ammo,
     * repairs and crew. The array was never fitted by any hull in the campaign; the only
     * way to own one was to buy it for $170,000, on a shift built around not having it.
     */
    {
      kind: 'spawn', trigger: { at: 300 }, tag: 'supply-1', vessel: 'supply',
      count: 1, rangeKm: 5, bearingDeg: -56, cargo: 'hydrophone',
    },
    {
      kind: 'radio', trigger: { at: 305 }, speaker: 'RIG CONTROL',
      text: 'Kestrel has your projector and a hydrophone set. Get her alongside.',
      read: '[urgent] Kestrel has your projector and a hydrophone set. [firmly] Get her alongside.',
    },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'supply-1', km: 2.4 }, timeout: 900 },
      tag: 'pack-a', vessel: 'gunboat', count: 1, rangeKm: 1.8, bearingDeg: -20,
    },
    /*
     * The shift ends on the hunt rather than on the convoy. The sub is a hostile, so the
     * end condition cannot resolve while she is alive — once the projector is aboard,
     * finding her *is* the remaining shift.
     */
    {
      kind: 'objective', trigger: { whenWithin: { tag: 'supply-1', km: 2.2 }, timeout: 900 },
      text: 'Projector aboard — find it and kill it',
    },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'supply-1', km: 1.3 }, timeout: 500 },
      tag: 'closer', vessel: 'gunboat', count: 1, rangeKm: 1.4, bearingDeg: -46,
    },
  ],
};

/**
 * Mini-subs hunting in earnest, in dawn fog.
 *
 * Now that the player has the projector and the hydrophone, the encounter becomes the
 * information problem it was designed as: a bearing, a widening uncertainty ellipse, and
 * a thirty-second periscope window that is the only moment gunfire can answer.
 */
const shift05: ShiftScript = {
  index: 5,
  name: 'Dawn Hunt',
  weather: 'dawnFog',
  seed: 'shift-08',
  maxDuration: 1700,
  beats: [
    { kind: 'objective', trigger: { at: 0 }, text: 'Two boats hunting the approach' },
    {
      kind: 'radio', trigger: { at: 3 }, speaker: 'RIG CONTROL',
      text: 'Same signature as last watch, and there are two of them now.',
      read: '[grim] Same signature as last watch. [pause] And there are two of them now.',
    },
    {
      kind: 'spawn', trigger: { at: 6 }, tag: 'tanker-1', vessel: 'tanker',
      count: 1, rangeKm: 6, bearingDeg: -24, cargo: 'oil',
    },
    // No surface pair to settle into: the shift opens on the thing itself. See ./openings.
    {
      kind: 'radio', trigger: { at: openingFor(5).radioAt }, speaker: 'SONAR',
      text: 'Contact. Bearing green five two, no surface return. It is already inside us.',
      read: '[tense] Contact. Bearing green five two, no surface return. [alarmed] It is already inside us.',
    },
    ...openingBeats(5).map((b) => ({ ...b, tag: 'sub-a' })),
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'tanker-1', km: 3.0 }, timeout: 800 },
      tag: 'sub-b', vessel: 'minisub', count: 1, rangeKm: 1.9, bearingDeg: -6,
    },
    {
      kind: 'objective', trigger: { whenWithin: { tag: 'tanker-1', km: 3.0 }, timeout: 800 },
      text: 'Hunt them — the periscope is your window',
    },
    /*
     * A surface screen, timed to be alive while the second boat is at periscope depth.
     *
     * The window is thirty seconds and it is the only moment gunfire can answer a
     * submarine, so the shift's question becomes literal: the periscope is up and there
     * are three skiffs on the tanker — which one gets the gun? Without the screen the
     * player simply waits for the window, and waiting is not a decision.
     */
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'tanker-1', km: 2.4 }, timeout: 800 },
      tag: 'screen', vessel: 'skiff', count: 3, rangeKm: 1.8, bearingDeg: -40, spreadDeg: 14,
    },
    {
      kind: 'radio', trigger: { whenWithin: { tag: 'tanker-1', km: 2.4 }, timeout: 800 },
      speaker: 'RADAR', text: 'Three on the surface, port quarter — while that thing is still down there.',
      read: '[urgent] Three on the surface, port quarter. [strained] While that thing is still down there.',
    },
    {
      kind: 'spawn', trigger: { at: 240 }, tag: 'supply-1', vessel: 'supply',
      count: 1, rangeKm: 5, bearingDeg: -60, cargo: 'ammo',
    },
    // Gunboats rather than a third skiff pack: the shift has enough small craft in it.
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'supply-1', km: 2.4 }, timeout: 900 },
      tag: 'pack-a', vessel: 'gunboat', count: 2, rangeKm: 2.0, bearingDeg: -16, spreadDeg: 16,
    },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'supply-1', km: 1.3 }, timeout: 500 },
      tag: 'closer', vessel: 'fac', count: 1, rangeKm: 1.5, bearingDeg: -50,
    },
  ],
};

/**
 * The corvette: the shift the torpedo was delivered for — and, since the cut, the
 * campaign's combined-arms climax.
 *
 * Autocannon rounds do 25% against her armour, so a stand-off gun duel is unwinnable by
 * design — the Shift-3 unlock pays off here or the player does not get through. Her
 * escorts exist to make the launch window costly rather than free, and the boat she
 * brought under her puts all three weapon systems live at once: torpedoes on the
 * corvettes, charges on the sub, the gun on everything fast. This is the loadout test
 * the campaign used to spend a whole extra shift on.
 */
const shift06: ShiftScript = {
  index: 6,
  name: 'Grey Hull',
  weather: 'storm',
  seed: 'shift-09',
  maxDuration: 1800,
  beats: [
    { kind: 'objective', trigger: { at: 0 }, text: 'Warship on the scope' },
    {
      kind: 'radio', trigger: { at: 3 }, speaker: 'RIG CONTROL',
      text: 'That is a corvette. Your gun will scratch her paint. Use the tubes.',
      read: '[grim] That is a corvette. [flat] Your gun will scratch her paint. [firmly] Use the tubes.',
    },
    {
      kind: 'spawn', trigger: { at: 8 }, tag: 'corvette-1', vessel: 'corvette',
      count: 1, rangeKm: 5.5, bearingDeg: -34,
    },
    {
      kind: 'spawn', trigger: { at: 20 }, tag: 'escort-a', vessel: 'gunboat',
      count: 2, rangeKm: 2.4, bearingDeg: -56, spreadDeg: 18,
    },
    {
      kind: 'radio', trigger: { at: 68 }, speaker: 'RADAR',
      text: 'She is putting boats in the water. Two of them, coming out from under her guns.',
      read: '[urgent] She is putting boats in the water. Two of them, coming out from under her guns.',
    },
    {
      kind: 'spawn', trigger: { at: 72 }, tag: 'escort-c', vessel: 'fac',
      count: 2, rangeKm: 2.2, bearingDeg: -50, spreadDeg: 16,
    },
    {
      kind: 'spawn', trigger: { at: 95 }, tag: 'tanker-1', vessel: 'tanker',
      count: 1, rangeKm: 6.5, bearingDeg: -12, cargo: 'oil',
    },
    {
      kind: 'radio', trigger: { at: 99 }, speaker: 'RIG CONTROL',
      text: 'Ardent is sailing into this whether we like it or not.',
      read: '[resigned] Ardent is sailing into this [pause] whether we like it or not.',
    },
    /*
     * The torpedo resupply, and it matters *because* cargo now lands at the Safe Line
     * rather than at the berth: saving her mid-battle reloads the tubes mid-battle. She
     * is worth defending for what she is carrying, which is the first time in the
     * campaign that sentence is literally true.
     */
    {
      kind: 'spawn', trigger: { at: 140 }, tag: 'supply-1', vessel: 'supply',
      count: 1, rangeKm: 5, bearingDeg: -62, cargo: 'torpedo',
    },
    {
      kind: 'radio', trigger: { at: 144 }, speaker: 'RIG CONTROL',
      text: 'Kestrel is running you two more fish. She will not survive being ignored.',
      read: '[urgent] Kestrel is running you two more fish. [warning] She will not survive being ignored.',
    },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'tanker-1', km: 2.6 }, timeout: 900 },
      tag: 'escort-b', vessel: 'fac', count: 2, rangeKm: 2.2, bearingDeg: -20, spreadDeg: 16,
    },
    /*
     * The boat under her (added with the 2026-07-30 cut — the third mini-sub shift).
     *
     * On a clock, early, exactly the way Something Under stages its boat — and by the
     * same measurement. A sub only becomes fightable once her ingress ends at the 950 m
     * periscope point, ninety-odd seconds after she spawns, so a spawn gated on the
     * convoy's late approach either torpedoes a tanker the corvettes have already
     * beaten to half health (2.4 km gate: lost her every steady run) or withdraws
     * without ever surfacing (1.6 km gate: never detected). Spawned at 0:50 she is up
     * mid-corvette-duel with the tanker still distant and whole: the periscope window
     * competes with the launch window, which is the entire point of her being here. The
     * tubes are the only answer to the corvettes, the projector is the only answer to
     * her, and both ask for the player's full attention at the same time.
     */
    {
      kind: 'radio', trigger: { at: 45 }, speaker: 'SONAR',
      text: 'I have... something. Bearing green four zero. No surface return.',
      read: '[hesitant] I have... [pause] something. Bearing green four zero. [quietly] No surface return.',
    },
    {
      kind: 'spawn', trigger: { at: 50 }, tag: 'sub-a', vessel: 'minisub',
      count: 1, rangeKm: 1.9, bearingDeg: -40,
    },
    /*
     * The second hull, and the shift's actual subject.
     *
     * The autocannon does 25% against corvette armour, so she is a torpedo problem or
     * she is nothing — and the player arrived with two fish and whatever they bought.
     * One corvette is a lesson in using the tubes; two is a question about how many are
     * left, which is a different and much better question. It is why Kestrel is on the
     * water at all.
     */
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'tanker-1', km: 2.0 }, timeout: 900 },
      tag: 'corvette-2', vessel: 'corvette', count: 1, rangeKm: 4.5, bearingDeg: -26,
    },
    {
      kind: 'radio', trigger: { whenWithin: { tag: 'tanker-1', km: 2.0 }, timeout: 900 },
      speaker: 'RADAR',
      text: 'Second hull in the rain. Same class as the first. How many fish do you have left?',
      read: '[alarmed] Second hull in the rain. Same class as the first. [pause] [quietly] How many fish do you have left?',
    },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'supply-1', km: 1.5 }, timeout: 700 },
      tag: 'closer', vessel: 'gunboat', count: 2, rangeKm: 1.5, bearingDeg: -44, spreadDeg: 16,
    },
  ],
};

/**
 * The Maw: first creature contact.
 *
 * Tentacles surface alongside a friendly and crush it over about half a minute unless
 * they are shot off. The body is never seen, which is the point — the shift introduces
 * the idea that something down there is large without ever showing how large.
 */
const shift07: ShiftScript = {
  index: 7,
  name: 'The Maw',
  weather: 'nightStorm',
  seed: 'shift-10',
  maxDuration: 1800,
  beats: [
    { kind: 'objective', trigger: { at: 0 }, text: 'Night storm — one arrival' },
    {
      kind: 'spawn', trigger: { at: 6 }, tag: 'tanker-1', vessel: 'tanker',
      count: 1, rangeKm: 5.5, bearingDeg: -20, cargo: 'oil',
    },
    // Opening probe — see ./openings.
    {
      kind: 'radio', trigger: { at: openingFor(7).radioAt }, speaker: 'RADAR',
      /*
       * The first mention of the creature in the whole campaign, so it cannot be a
       * callback.
       *
       * It read "not like that thing", which presumes the player has met something. They
       * have not: this shift is where the Maw first appears, later in this very shift.
       *
       * The first rewrite kept "moving like men" and Adam rejected that too, for two
       * reasons that are really one. The contrast only parses if you already know what
       * the other thing moves like — and worse, it had the operator holding knowledge the
       * player does not have. He is the player's eyes, not a narrator standing outside the
       * shift; anything he knows about the water, they should know.
       *
       * So the dread comes from what he *cannot* tell them. He reports exactly what is
       * visible, and then says the storm has taken his sonar — which is information the
       * player can act on, is true to a night storm, and leaves them equally blind. The
       * creature is foreshadowed by the shape of the gap rather than by a hint only one
       * of them is holding.
       */
      text: 'Two contacts through the squall, port bow. Small craft, coming for the platform. The storm has the sonar blind — anything under the surface, we meet when it arrives.',
      read: '[tense] Two contacts through the squall, port bow. [pause] Small craft, coming for the platform. [quietly] The storm has the sonar blind. [uneasy] Anything under the surface, we meet when it arrives.',
    },
    ...openingBeats(7),
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'tanker-1', km: 3.2 }, timeout: 800 },
      tag: 'pack-a', vessel: 'skiff', count: 2, rangeKm: 1.8, bearingDeg: -54, spreadDeg: 14,
    },
    {
      kind: 'radio', trigger: { whenWithin: { tag: 'tanker-1', km: 2.2 }, timeout: 900 },
      speaker: 'RADAR', text: 'Contact alongside Ardent. It did not come from anywhere.',
      read: '[alarmed] Contact alongside Ardent. [unsettled] It did not come from anywhere.',
    },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'tanker-1', km: 2.2 }, timeout: 900 },
      tag: 'maw-a', vessel: 'tentacle', count: 3, rangeKm: 1.9, bearingDeg: -22, spreadDeg: 8,
    },
    {
      kind: 'objective', trigger: { whenWithin: { tag: 'tanker-1', km: 2.2 }, timeout: 900 },
      text: 'Cut them off her',
    },
    /*
     * The second emergence, and the only place in the campaign where `whenResolved` is
     * the right gate.
     *
     * Everywhere else it punishes accuracy — clear a wave fast and the next one simply
     * waits. Here the second pulse *answering the first cut* is the horror beat: the
     * player severs three limbs, the water goes quiet, and more come up. Measured at
     * skilled pace the first creature contact in the game lasted nine seconds; this is
     * what makes it an encounter instead of an anecdote.
     */
    {
      kind: 'radio', trigger: { whenResolved: { tag: 'maw-a' }, timeout: 240 },
      speaker: 'SONAR', text: 'You cut them. More are coming up. Count says more than before.',
      read: '[shaken] You cut them. [pause] More are coming up. [quietly] Count says more than before.',
    },
    {
      kind: 'spawn', trigger: { whenResolved: { tag: 'maw-a' }, timeout: 240 },
      tag: 'maw-b', vessel: 'tentacle', count: 3, rangeKm: 1.6, bearingDeg: -38, spreadDeg: 10,
    },
    /*
     * Pirates and the Maw in the same water, without a line of AI changing. They are not
     * brave, they are opportunists — and the tanker is busy being eaten.
     */
    {
      kind: 'radio', trigger: { whenWithin: { tag: 'tanker-1', km: 1.4 }, timeout: 900 },
      speaker: 'RADAR', text: 'Two small craft closing on her. They waited for it to finish.',
      read: '[disgusted] Two small craft closing on her. [pause] They waited for it to finish.',
    },
    {
      kind: 'spawn', trigger: { whenWithin: { tag: 'tanker-1', km: 1.4 }, timeout: 900 },
      tag: 'scavengers', vessel: 'skiff', count: 2, rangeKm: 1.4, bearingDeg: -60, spreadDeg: 12,
    },
  ],
};

/**
 * Shift 8: the Kraken.
 *
 * Scripted only as far as the arrival. The encounter's phases are driven by the boss's
 * own state machine in `sim/kraken.ts` rather than by beats, because a boss that follows
 * a timeline is a cutscene — the phases have to answer what the player does.
 *
 * Weather opens at the storm's peak and drops to an eerie calm as she arrives: the sea
 * going quiet is the tell, and it is the one moment in the game where less weather is
 * more frightening than more.
 *
 * The approach runs 2:50 to the reveal, down from 5:50, and every beat in it survived.
 * The saving is entirely structural: the two long effects — the sea flattening and the
 * dread — used to be queued one behind the other, ninety seconds and then a hundred and
 * twenty, with the creature arriving only after the calm had finished landing. They
 * overlap now. It is already underneath while the storm is dying, which is shorter
 * *and* better: the sea going quiet stops being a thing that happens before it turns up
 * and becomes the first evidence of it. (2:50 rather than 2:20 is Adam's playtest
 * timing — thirty seconds of flat water between the sonar report and the surfacing.)
 *
 * That only works because the creature is genuinely unseen during `dread` — see
 * `sim/kraken.ts`. Surfaced, as it used to be, it would spend the whole transformation
 * sitting in plain sight and there would be nothing left to build to.
 */
const shift08: ShiftScript = {
  index: 8,
  name: 'Deepwater',
  weather: 'severeStorm',
  seed: 'shift-12',
  maxDuration: 2400,
  /*
   * The one shift that is written to be waited through. See `ShiftScript.pacing`: these
   * beats are the game's slowest, most deliberate build, and the lull compression used to
   * run the whole thing in sixty-two seconds.
   */
  pacing: 'authored',
  beats: [
    { kind: 'objective', trigger: { at: 0 }, text: 'Ride out the storm' },
    {
      kind: 'radio', trigger: { at: 4 }, speaker: 'RIG CONTROL',
      text: 'Storm peak in twenty minutes. Nothing is sailing in this.',
      read: '[weary] Storm peak in twenty minutes. [pause] Nothing is sailing in this.',
    },
    {
      kind: 'spawn', trigger: { at: 8 }, tag: 'pack-a', vessel: 'skiff',
      count: 3, rangeKm: 1.9, bearingDeg: -50, spreadDeg: 14,
    },
    // The sea starts going over as the last of them goes down: seventy seconds of storm
    // draining out of the water, finished by 1:48.
    { kind: 'weather', trigger: { at: 38 }, preset: 'night', overSeconds: 70 },
    /*
     * It arrives under the storm rather than after it, and spends the whole flattening
     * below the surface: nothing on radar, nothing to see but the shape the renderer
     * draws under the water, and nothing worth shooting. Dread ends at 2:50.
     */
    {
      kind: 'spawn', trigger: { at: 42 }, tag: 'kraken', vessel: 'kraken',
      count: 1, rangeKm: 1.2, bearingDeg: -30,
    },
    {
      kind: 'objective', trigger: { at: 110 }, text: 'Something is coming up',
    },
    /*
     * The sea report, said after the sea has actually done it — and then a long beat
     * before the answer.
     *
     * This used to fire at 0:38 — the same instant the seventy-second flattening was
     * *ordered* — so the spotter announced a calm the player was still watching a storm
     * in. Moved to 2:05 it was still crowding the reveal; Adam's playtest verdict was
     * fifteen seconds later again, with the creature held back a further fifteen "so
     * the player has the time to notice the shift". So: the blend finishes at 1:48,
     * this lands at 2:20, and the creature surfaces at 2:50 — thirty seconds of flat
     * dead water between the sentence and the thing it was about. By construction: the
     * spawn is at 0:42 and `KRAKEN.dreadSeconds` is 128.
     */
    {
      kind: 'radio', trigger: { at: 140 }, speaker: 'SONAR',
      text: 'Sir. The sea just went flat. All of it.',
      read: '[unnerved] Sir. [pause] The sea just went flat. [whispers] All of it.',
    },
    /*
     * The dread's last word, and it does not claim to know where the thing is.
     *
     * This used to say "it is under us. Not out there. Under us." two minutes in, while
     * the creature was still 1.2 km out on the bearing the player was watching — the
     * spotter asserting the one fact he could not have, about a position that was not
     * true yet. Adam: the line should not land until just before the limbs come. It
     * belongs to the assault now (`KRAKEN_LINES.tentacles`), where the body genuinely is
     * beneath the platform, and what is left here is only what sonar can honestly report.
     */
    {
      kind: 'radio', trigger: { at: 100 }, speaker: 'SONAR',
      text: 'It is all noise down there. I have it, and I cannot tell you where.',
      read: '[frightened] It is all noise down there. [pause] I have it, and [whispers] I cannot tell you where.',
    },
  ],
};

export const LATE_SHIFTS: readonly ShiftScript[] = [
  shift03, shift04, shift05, shift06, shift07, shift08,
];
